import * as SelectPrimitive from "@radix-ui/react-select"
import { cva, type VariantProps } from "class-variance-authority"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import * as React from "react"

import { useFieldProps } from "@/components/ui/form-field"
import { cn } from "@/lib/utils"

/*
   Replaces the native <select>, which renders OS chrome ignoring design
   tokens (macOS blue highlight; different again on Windows).

   For more than ~10 options, or when the person knows what they want
   before they open it, use Combobox instead.
 */

export const Select = SelectPrimitive.Root
export const SelectGroup = SelectPrimitive.Group
export const SelectValue = SelectPrimitive.Value

const trigger = cva(
  [
    "flex w-full items-center justify-between gap-2",
    "rounded-md border bg-surface text-left",
    "transition-colors duration-120 ease-out",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
    "focus-visible:outline-[color:var(--brand)]",
    "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled",
    "data-[placeholder]:text-text-disabled",
    "[&>span]:min-w-0 [&>span]:truncate",
  ],
  {
    variants: {
      selectSize: {
        sm: "h-7.5 px-2.5 text-body-sm [&_svg]:size-3.5",
        md: "h-9 px-3 text-body [&_svg]:size-4",
        lg: "h-10.5 px-3.5 text-body [&_svg]:size-4",
      },
      invalid: {
        true: "border-err bg-err-tint/40 text-text",
        false: "border-border text-text hover:border-border-strong",
      },
      mono: { true: "font-id tabular-nums", false: "" },
    },
    defaultVariants: { selectSize: "md", invalid: false, mono: false },
  },
)

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof trigger> {}

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(function SelectTrigger({ className, children, selectSize, invalid, mono, ...rest }, ref) {
  const props = useFieldProps(rest)
  const isInvalid =
    invalid ?? (props["aria-invalid"] === true || props["aria-invalid"] === "true")

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(trigger({ selectSize, invalid: Boolean(isInvalid), mono }), className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="shrink-0 text-text-3" aria-hidden="true" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
})

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectContent({ className, children, position = "popper", ...props }, ref) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={6}
        className={cn(
          "z-modal max-h-[min(24rem,var(--radix-select-content-available-height))]",
          "w-[var(--radix-select-trigger-width)] overflow-hidden",
          "rounded-lg border border-border bg-surface p-1 shadow-e3",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          "data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-1",
          "duration-180 motion-reduce:animate-none",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center bg-surface text-text-3">
          <ChevronUp className="size-3.5" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center bg-surface text-text-3">
          <ChevronDown className="size-3.5" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})

export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(function SelectLabel({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Label
      ref={ref}
      className={cn(
        "px-2 pb-1 pt-2.5 font-id text-eyebrow uppercase text-text-3",
        className,
      )}
      {...props}
    />
  )
})

export interface SelectItemProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  /**
   * One line explaining what choosing this does. A native <option> can't
   * carry this, which is why role and severity pickers end up cryptic.
   */
  description?: React.ReactNode
  /** Leading glyph — a severity dot, a side marker, an avatar. */
  icon?: React.ReactNode
}

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(function SelectItem({ className, children, description, icon, ...props }, ref) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "group relative flex cursor-pointer select-none items-start gap-2",
        "rounded-md px-2 py-1.5 outline-none",
        "text-body-sm text-text",
        "transition-colors duration-120 ease-out",
        "data-[highlighted]:bg-surface-sunken",
        "data-[state=checked]:text-text",
        "data-[disabled]:pointer-events-none data-[disabled]:text-text-disabled",
        className,
      )}
      {...props}
    >
      <span
        className="mt-[3px] flex size-3.5 shrink-0 items-center justify-center text-brand opacity-0 group-data-[state=checked]:opacity-100"
        aria-hidden="true"
      >
        <SelectPrimitive.ItemIndicator>
          <Check className="size-3.5" />
        </SelectPrimitive.ItemIndicator>
      </span>
      {icon && (
        <span className="mt-[3px] shrink-0 text-text-3 [&_svg]:size-3.5" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <SelectPrimitive.ItemText asChild>
          <span className="block truncate font-medium text-text group-data-[state=checked]:text-brand">
            {children}
          </span>
        </SelectPrimitive.ItemText>
        {description && (
          <span className="mt-0.5 block text-caption leading-snug text-text-3 line-clamp-2">
            {description}
          </span>
        )}
      </span>
    </SelectPrimitive.Item>
  )
})

export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(function SelectSeparator({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Separator
      ref={ref}
      className={cn("my-1 h-px bg-border-soft", className)}
      {...props}
    />
  )
})

